/**
 * EditableList — bullet list with editable items + add/remove. Mockup §3.
 *
 * Used for `needs` (numbered/bulleted item list). Each row is an inline
 * editable input; "+ Add item" affordance appears at the bottom.
 */

import { useEffect, useRef, useState } from 'react'
import { makeStyles, tokens, Button } from '@fluentui/react-components'
import { Add20Regular, Dismiss12Regular } from '@fluentui/react-icons'
import { useToast } from './ToastProvider'
import { bayerColors } from '../theme'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 0',
  },
  bullet: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: bayerColors.navy,
    flexShrink: 0,
  },
  input: {
    flex: 1,
    minWidth: 0,
    fontSize: '13px',
    padding: '4px 8px',
    borderRadius: '4px',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: 'transparent',
    borderLeftColor: 'transparent',
    outline: 'none',
    background: 'transparent',
    transitionProperty: 'background, border-color',
    transitionDuration: '100ms',
    ':hover': {
      background: bayerColors.navyLight,
      borderTopColor: '#54749a',
      borderRightColor: '#54749a',
      borderBottomColor: '#54749a',
      borderLeftColor: '#54749a',
    },
    ':focus': {
      background: '#FFFFFF',
      borderTopColor: bayerColors.navyMid,
      borderRightColor: bayerColors.navyMid,
      borderBottomColor: bayerColors.navyMid,
      borderLeftColor: bayerColors.navyMid,
      boxShadow: '0 0 0 3px rgba(43,107,166,0.12)',
    },
  },
  removeBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#616161',
    padding: '2px',
    ':hover': { color: '#b10e1c' },
  },
  addBtn: {
    marginTop: tokens.spacingVerticalXS,
    alignSelf: 'flex-start',
  },
})

export interface EditableListProps {
  values: string[]
  path: string
  onSave: (path: string, values: string[]) => void
  placeholder?: string
  addLabel?: string
  disabled?: boolean
}

export function EditableList({
  values,
  path,
  onSave,
  placeholder = 'Item',
  addLabel = '+ Add item',
  disabled = false,
}: EditableListProps) {
  const styles = useStyles()
  const toast = useToast()
  const [drafts, setDrafts] = useState<Record<number, string>>({})
  // Track locally-added empty rows that haven't been committed yet
  const [pendingCount, setPendingCount] = useState(0)
  const newInputRef = useRef<HTMLInputElement>(null)

  // All items = props values + pending empty rows
  const allItems = [...values, ...Array(pendingCount).fill('')]

  // When values prop changes (e.g. after save + refetch), clear pending rows
  // that have been incorporated into the saved values
  useEffect(() => {
    setPendingCount(0)
    setDrafts({})
  }, [values.length])

  function update(idx: number, next: string) {
    setDrafts((prev) => ({ ...prev, [idx]: next }))
  }

  function commit(idx: number) {
    const draft = drafts[idx]
    if (draft === undefined) {
      // For pending empty rows with no typed content, just remove
      if (idx >= values.length && pendingCount > 0) {
        setPendingCount((c) => Math.max(0, c - 1))
      }
      return
    }
    const trimmed = draft.trim()
    if (idx < values.length) {
      // Editing existing item
      if (trimmed === values[idx]) {
        setDrafts((prev) => { const { [idx]: _, ...rest } = prev; return rest })
        return
      }
      if (trimmed === '') {
        onSave(path, values.filter((_, i) => i !== idx))
      } else {
        onSave(path, values.map((v, i) => (i === idx ? trimmed : v)))
      }
    } else {
      // Committing a pending new item
      if (trimmed !== '') {
        onSave(path, [...values, trimmed])
      }
      setPendingCount((c) => Math.max(0, c - 1))
    }
    setDrafts((prev) => { const { [idx]: _, ...rest } = prev; return rest })
  }

  function remove(idx: number) {
    if (idx >= values.length) {
      // Remove pending row
      setPendingCount((c) => Math.max(0, c - 1))
      setDrafts((prev) => { const { [idx]: _, ...rest } = prev; return rest })
    } else {
      // Undo rather than a confirmation (#1245) — the whole array is sent on
      // every save, so restoring it is the same call with the old value.
      const before = values
      const removed = values[idx]
      onSave(path, values.filter((_, i) => i !== idx))
      toast.showToast({
        title: removed ? `Removed “${removed}”` : 'Item removed',
        action: { label: 'Undo', onClick: () => onSave(path, before) },
      })
    }
  }

  function add() {
    setPendingCount((c) => c + 1)
    // Focus the new input after render
    setTimeout(() => newInputRef.current?.focus(), 0)
  }

  return (
    <div>
      <ul className={styles.root}>
        {allItems.map((v, idx) => (
          <li key={idx} className={styles.row}>
            <span className={styles.bullet} />
            <input
              ref={idx === allItems.length - 1 && pendingCount > 0 ? newInputRef : undefined}
              type="text"
              className={styles.input}
              value={drafts[idx] ?? v}
              placeholder={placeholder}
              disabled={disabled}
              onChange={(e) => update(idx, e.target.value)}
              onBlur={() => commit(idx)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  ;(e.currentTarget as HTMLInputElement).blur()
                } else if (e.key === 'Escape') {
                  setDrafts((prev) => { const { [idx]: _, ...rest } = prev; return rest })
                  if (idx >= values.length) setPendingCount((c) => Math.max(0, c - 1))
                  ;(e.currentTarget as HTMLInputElement).blur()
                }
              }}
            />
            {!disabled && (
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => remove(idx)}
                aria-label={`Remove item ${idx + 1}`}
              >
                <Dismiss12Regular />
              </button>
            )}
          </li>
        ))}
      </ul>
      {!disabled && (
        <Button
          appearance="subtle"
          size="small"
          icon={<Add20Regular />}
          className={styles.addBtn}
          onClick={add}
        >
          {addLabel}
        </Button>
      )}
    </div>
  )
}
